import { useCallback, useEffect, useState } from 'react';
import EntityCombobox from '@/components/EntityCombobox';
import PersonFormDialog from '@/components/entities/PersonFormDialog';
import api from '@/lib/api';

/**
 * Searchable picker for a Person, with an inline "New person" action.
 *
 * Controlled list mode: pass `persons` (array) + `onCreated(person)` and the
 * parent owns the list. Uncontrolled: omit both and the picker fetches/manages
 * its own list.
 */
const PersonPicker = ({
  value, onChange, persons, onCreated, excludeIds = [],
  placeholder = 'Select a person…', testId = 'person-picker',
}) => {
  const controlled = Array.isArray(persons);
  const [ownList, setOwnList] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [initialName, setInitialName] = useState('');
  const list = controlled ? persons : ownList;

  const fetchPersons = useCallback(async () => {
    try {
      const res = await api.get('/persons');
      setOwnList(res.data);
    } catch {
      /* handled by page-level toast elsewhere */
    }
  }, []);

  useEffect(() => {
    if (!controlled) fetchPersons();
  }, [controlled, fetchPersons]);

  const items = list
    .filter((p) => !excludeIds.includes(p.id) || p.id === value)
    .map((p) => ({ value: p.id, label: p.name, sublabel: p.email || '' }));

  const handleSaved = (person) => {
    if (controlled) onCreated?.(person);
    else setOwnList((prev) => [person, ...prev.filter((p) => p.id !== person.id)]);
    onChange(person.id);
  };

  return (
    <>
      <EntityCombobox
        items={items}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        searchPlaceholder="Search people…"
        emptyText="No people found."
        createLabel="New person"
        onCreate={(name) => { setInitialName(name || ''); setShowCreate(true); }}
        testId={testId}
      />
      <PersonFormDialog open={showCreate} onOpenChange={setShowCreate} onSaved={handleSaved} initialName={initialName} />
    </>
  );
};

export default PersonPicker;
